import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import AddressBook from '@medflex/components/stories/AddressBook';
import { useTranslation } from 'react-i18next';
import { USER_TYPES } from '@medflex/utils/constants';
import isSupportPatient from '@medflex/utils/isSupportPatient';
import isSelf from '@medflex/utils/isSelf';
import matchesSearchTerm from '@medflex/utils/matchesSearchTerm';
import joinUmlauts from '@medflex/utils/joinUmlauts';
import ContactList from '@medflex/components/stories/ContactList';
import Spinner from '@medflex/components/stories/Spinner';
import isPrimaryClickEvent from '@medflex/utils/isPrimaryClickEvent';
import { trackEvent, TRACKING_ACTIONS } from '@medflex/utils/Tracking';
import { Role, Subtitle, Title } from '@medflex/components/stories/HighlightableInfos';
import WindowingContainer from '@medflex/components/stories/WindowingContainer';
import getAreaOfActivity from '@medflex/utils/getAreaOfActivity';
import useModal from '../../hooks/useModal';
import useOnlineStatus from '../../hooks/useOnlineStatus';
import useLocationSearchParams from '../../../hooks/useLocationSearchParams';
import getSubtitle from '../../../utils/getSubtitle';
import useUserProfile from '../../hooks/useUserProfile';
import useSessionChats from '../../hooks/useSessionChats';
import useAddressBookContacts from '../../hooks/useAddressBookContacts';
import mapSessionChatToContact from '../../../utils/mapSessionChatToContact';
import AddressBookTitleBarModule from './AddressBookTitleBarModule';
import AddressBookActionMenu from './AddressBookActionMenu';
import InvitationBarModule from './InvitationBarModule';
import EmptyStateModule from './EmptyStateModule';
import { MODALS } from '../../hooks/useIsModal';

const sortByFullNameWithTitle = (a, b) => {
  const nameA = a.user.fullNameWithTitle.toUpperCase();
  const nameB = b.user.fullNameWithTitle.toUpperCase();
  if (nameA < nameB) return -1;
  if (nameA > nameB) return 1;
  return 0;
};

const PhysicianAddressBookModule = () => {
  const { staff, isLoading: addressBookContactsLoading, all } = useAddressBookContacts();
  const { sort: filter, order = 'az' } = useLocationSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const searchFieldUsedRef = useRef(null);
  const { userProfile } = useUserProfile();
  const { t } = useTranslation('global');
  const { onlineUserIds } = useOnlineStatus();
  const { chats: sessionChats, isLoading: sessionChatsLoading } = useSessionChats();
  const history = useHistory();
  const { contactId: selectedContactId } = useParams();
  const { search } = useLocation();
  const { open } = useModal();

  const contactsLoading = sessionChatsLoading || addressBookContactsLoading;

  const handleSearchTermInput = value => {
    setSearchTerm(value);

    if (!searchFieldUsedRef.current && value) {
      trackEvent({
        zone: 'addressbook',
        action: TRACKING_ACTIONS.SUBMIT,
        actionText: 'search field used | physician/staff',
      });
      searchFieldUsedRef.current = true;
    }
  };

  const normalizedSessionChats = useMemo(
    () =>
      sessionChats
        .filter(({ id }) => !['s-i1'].includes(id))
        .map(
          mapSessionChatToContact({
            userId: userProfile.id,
            teamSubTitle: t('messenger:team_chat_subtitle'),
          }),
        ),
    [sessionChats, t, userProfile.id],
  );

  const filteredAddressBookEntries = useMemo(() => {
    if (!contactsLoading && (all.length > 0 || normalizedSessionChats.length > 0)) {
      // display teamChat only if team consists of at least 2 staffs or teamChat has content
      const filteredSessionChats =
        staff.length > 1
          ? normalizedSessionChats
          : normalizedSessionChats.filter(c => !(c.type === 'team-chat' && !c.messages.lastAt));

      return [
        ...(typeof filter === 'undefined' || filter === 'all' ? filteredSessionChats : []),
        ...(filter === 'groups' ? normalizedSessionChats.filter(c => c.type !== 'team-chat') : []),
        ...(filter === 'staff' ? filteredSessionChats : []),
        ...(filter !== 'groups' ? all : []),
      ].filter(addressBookEntry => {
        if (isSelf(addressBookEntry.user)) return false;

        if (
          filter === 'physicians' &&
          (addressBookEntry.type !== 'physician' ||
            !!addressBookEntry.archivedAt ||
            addressBookEntry.isTeam)
        )
          return false;

        if (
          filter === 'staff' &&
          (!(addressBookEntry.isTeam || addressBookEntry.isTeamChat) ||
            !!addressBookEntry.archivedAt)
        )
          return false;

        if (
          filter === 'patients' &&
          (addressBookEntry.type !== 'patient' ||
            !addressBookEntry.pairedAt ||
            isSupportPatient(addressBookEntry.user) ||
            !!addressBookEntry.archivedAt)
        )
          return false;

        if (
          filter === 'invited' &&
          (addressBookEntry.type !== 'patient' || !!addressBookEntry.pairedAt)
        )
          return false;

        if (filter === 'archive' && !addressBookEntry.archivedAt) return false;

        if (
          (!filter || filter === 'all') &&
          (!!addressBookEntry.archivedAt || addressBookEntry.type === 'team-chat')
        )
          return false;

        return true;
      });
    }
    return [];
  }, [contactsLoading, all, normalizedSessionChats, staff, filter]);

  const sortedAddressBookEntries = useMemo(() => {
    const rs = filteredAddressBookEntries.sort(sortByFullNameWithTitle);
    return order === 'az' ? rs : [...rs].reverse();
  }, [order, filteredAddressBookEntries]);

  const queriedAddressBookEntries = useMemo(
    () =>
      sortedAddressBookEntries.filter(addressBookEntry => {
        const subtitle = getSubtitle(addressBookEntry, userProfile, t);
        const areaOfActivity = getAreaOfActivity(addressBookEntry);
        const searchableProperties = [
          addressBookEntry.user.fullNameWithTitle,
          addressBookEntry.user.jobTitle,
          addressBookEntry.user.dateOfBirth,
          addressBookEntry.user.workplace,
          addressBookEntry.user.workArea,
          addressBookEntry.user.email,
          addressBookEntry.user.phoneNumbers?.map(({ number }) => number).join(','),
          addressBookEntry.user.pvsId,
          addressBookEntry.note,
          subtitle,
          areaOfActivity,
        ];
        return matchesSearchTerm(searchTerm, searchableProperties);
      }),
    [sortedAddressBookEntries, userProfile, t, searchTerm],
  );

  const { groupedContacts = [], countWithoutSupport = 0 } = useMemo(() => {
    if (queriedAddressBookEntries.length > 0) {
      return queriedAddressBookEntries.reduce(
        (acc, addressBookEntry) => {
          const subtitle = getSubtitle(addressBookEntry, userProfile, t);
          return {
            ...acc,
            countWithoutSupport: isSupportPatient(addressBookEntry.user)
              ? acc.countWithoutSupport
              : acc.countWithoutSupport + 1,
            groupedContacts: [
              ...acc.groupedContacts,
              {
                ...addressBookEntry,
                ...(addressBookEntry.type !== 'session-chat' && {
                  user: {
                    ...addressBookEntry.user,
                    subtitle,
                  },
                  isOnline: onlineUserIds.includes(addressBookEntry.user.id),
                }),
              },
            ],
          };
        },
        { groupedContacts: [], countWithoutSupport: 0 },
      );
    }
    return { groupedContact: [], countWithoutSupport: 0 };
  }, [all, onlineUserIds, queriedAddressBookEntries, t, userProfile]);

  const handleClickContact =
    ({ id, user, isTeam, pairedAt, type }) =>
    event => {
      if (!event.defaultPrevented && isPrimaryClickEvent(event)) {
        if (type === USER_TYPES.PATIENT) {
          event.preventDefault();
          history.push(`/address-book/${id}/overview${search}`);
          return;
        }

        if (type === USER_TYPES.STAFF && typeof pairedAt === 'undefined') return;
        event.preventDefault();
        history.push(`/address-book/${user.id}${isTeam ? '?isTeamMember=true' : ''}`);
      }
    };

  const handleOpenMainContact = ({ id }) => {
    history.push(`/chat/${id}`);
  };

  const handleClickAdd = (value, key = 'modal') => {
    open({ [key]: value });
  };

  const handleOpenContactInfo = useCallback(
    ({ id, type, user }) =>
      event => {
        if (type === USER_TYPES.PATIENT) {
          event.preventDefault();
          history.push(`/address-book/${id}/overview${search}`);
          return;
        }

        open({ modal: MODALS.contact_info, userId: user.id });
      },
    [history, open, search],
  );

  return (
    <div
      data-component="PhysicianAddressBookModule"
      className="flex flex-1 flex-row overflow-y-auto xl:min-w-96 xl:max-w-96"
    >
      <div className="flex flex-col border-r h-full overflow-hidden">
        <AddressBook>
          <AddressBookTitleBarModule
            isGroupsHidden={normalizedSessionChats.length === 0}
            onSearchTermChanged={handleSearchTermInput}
          />
          <WindowingContainer list={groupedContacts}>
            {({ windowedList, ref, listWindowLimit }) => {
              const addressBookEntries = windowedList.reduce((memo, entry) => {
                const letter = joinUmlauts(entry.user.fullNameWithTitle.toLowerCase().charAt(0));
                return {
                  ...memo,
                  [letter]: [...(memo[letter] || []), entry],
                };
              }, {});

              return (
                <div ref={ref} className="flex-1 overflow-y-auto h-full">
                  {countWithoutSupport === 0 && (
                    <EmptyStateModule onClick={handleClickAdd} sort={filter} />
                  )}
                  {Object.keys(addressBookEntries).map(letter => (
                    <div key={letter}>
                      <div className="text-xl font-semibold px-3 pt-3">{letter.toUpperCase()}</div>
                      <ContactList>
                        {addressBookEntries[letter].map(contact => (
                          <ContactList.Item
                            className={
                              contact.type === USER_TYPES.STAFF && !contact.pairedAt
                                ? 'cursor-not-allowed'
                                : ''
                            }
                            key={contact.user.id}
                            contactUserId={contact.user.id}
                            avatarSrc={contact.user.avatar}
                            avatarInitials={contact.user.initials}
                            isOnline={contact.isOnline}
                            selected={selectedContactId && selectedContactId === contact.id}
                            role={<Role contact={contact} searchTerm={searchTerm} />}
                            title={<Title contact={contact} searchTerm={searchTerm} />}
                            subtitle={
                              <Subtitle
                                contact={contact}
                                searchTerm={searchTerm}
                                onClickContactInfo={handleOpenContactInfo(contact)}
                                onClickMainContact={() =>
                                  handleOpenMainContact(contact.mainContact)
                                }
                              />
                            }
                            enabled={!!contact.pairedAt || contact.type === USER_TYPES.PHYSICIAN}
                            deactivated={contact.user.deactivated}
                            onClick={handleClickContact(contact)}
                            ActionMenu={props =>
                              AddressBookActionMenu({
                                contact,
                                isOrgaContact:
                                  userProfile.attributes.organization &&
                                  userProfile.attributes.organization.id ===
                                    contact.user.organizationId,
                                ...props,
                              })
                            }
                          />
                        ))}
                      </ContactList>
                    </div>
                  ))}
                  {listWindowLimit < groupedContacts.length && (
                    <div className="mb-2 w-full text-center">
                      <Spinner size="lg" />
                    </div>
                  )}
                </div>
              );
            }}
          </WindowingContainer>
          <InvitationBarModule onClick={handleClickAdd} sort={filter} />
        </AddressBook>
      </div>
    </div>
  );
};

PhysicianAddressBookModule.propTypes = {};

export default PhysicianAddressBookModule;
